import type { UserProfileDraft } from "@/modules/users/entities/usersEntity"
import { type SimpleFormik } from "@/typings/formik"
import {
  type AddressFormikType,
  convertAddressFromFormik,
  convertAddressToFormik,
} from "@modules/address/addressFormikType"
import { word } from "@modules/core/utils/i18n"
import { GenderCategory } from "@modules/profile/publicProfile/gateway/publicProfileDto"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import { nameRegExp, usernameRegExp } from "@modules/utils/regexp"
import { type HUUnit } from "@modules/utils/unitType"
import dayjs from "dayjs"
import parsePhoneNumber, { type CountryCode } from "libphonenumber-js"
import * as Yup from "yup"

export type ProfileFormTabProps = {
  isFormFieldInValid: (name: ProfileInputKey) => boolean
  getFormErrorMessage: (name: ProfileInputKey, errorMessage?: string) => React.JSX.Element | undefined
  formik: SimpleFormik<UserFormikType, ProfileInputKey>
}

export type ProfileInputKey =
  | "firstname"
  | "lastname"
  | "username"
  | "birthdate"
  | "email"
  | "unit"
  | "birthdateVisibility"
  | "crossfitStartDate"
  | "crossfitStartDateVisibility"
  | "genderCategory"
  | "genderCategoryVisibility"
  | "biography"
  | "job"
  | "jobVisibility"
  | "nationality"
  | "nationalityVisibility"
  | "address"
  | "postalCode"
  | "city"
  | "country"
  | "phoneNumberCountryCode"
  | "phoneNumber"
  | "emergencyInformation"
  | "weight"
  | "weightVisibility"
  | "height"
  | "heightVisibility"

export function identityTabValidation() {
  return Yup.object({
    firstname: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .matches(nameRegExp, word("form.invalid.firstname"))
      .required(word("form.required.firstname")),
    lastname: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .matches(nameRegExp, word("form.invalid.lastname"))
      .required(word("form.required.lastname")),
    username: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .matches(usernameRegExp, word("form.invalid.username"))
      .required(word("form.required.username")),
    birthdate: Yup.date().required(word("form.required.birthDate")),
  })
}
export function crossfitTabValidation() {
  return Yup.object({
    crossfitStartDate: Yup.date().nullable(),
    crossfitStartDateVisibility: Yup.boolean(),
    genderCategory: Yup.string().max(30, word("form.maximum.characters", { count: 30 })),
    genderCategoryVisibility: Yup.boolean(),
  })
}
export function biographyTabValidation() {
  return Yup.object({
    biography: Yup.string()
      .max(500, word("form.maximum.characters", { count: 500 }))
      .nullable(),
    job: Yup.string().max(100, word("form.maximum.characters", { count: 100 })),
    jobVisibility: Yup.boolean(),
    nationality: Yup.string().max(100, word("form.maximum.characters", { count: 100 })),
    nationalityVisibility: Yup.boolean(),
  })
}
export function contactTabValidation() {
  return Yup.object({
    email: Yup.string().email(word("box.form.email.error")),
    phoneNumber: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .test("phoneNumber", word("box.form.phone.error"), (value, context) => {
        if (value) {
          const parse = parsePhoneNumber(value, context.parent.phoneNumberCountryCode as CountryCode)
          return !!parse?.isValid()
        }
        return true
      }),
    phoneNumberCountryCode: Yup.string().max(2, word("form.maximum.characters", { count: 2 })),
    address: Yup.string().max(255, word("form.maximum.characters", { count: 255 })),
    postalCode: Yup.string().max(10, word("form.maximum.characters", { count: 10 })),
    city: Yup.string().max(64, word("form.maximum.characters", { count: 64 })),
    country: Yup.string().max(255, word("form.maximum.characters", { count: 255 })),
    emergencyInformation: Yup.string()
      .max(255, word("form.maximum.characters", { count: 255 }))
      .nullable(),
  })
}
export function anatomyTabValidation() {
  return Yup.object({
    height: Yup.number()
      .nullable()
      .min(0, word("form.number.minimum.value", { count: 0 }))
      .max(300, word("form.number.maximum.value", { count: 300 })),
    heightVisibility: Yup.boolean(),
    weight: Yup.number()
      .nullable()
      .min(0, word("form.number.minimum.value", { count: 0 }))
      .max(1000, word("form.number.maximum.value", { count: 1000 })),
    weightVisibility: Yup.boolean(),
  })
}

export function getValidationSchema(pageNumber?: string) {
  switch (pageNumber) {
    case "0":
      return identityTabValidation()
    case "1":
      return crossfitTabValidation()
    case "2":
      return biographyTabValidation()
    case "3":
      return contactTabValidation()
    case "4":
      return anatomyTabValidation()
    default:
      return identityTabValidation()
        .concat(crossfitTabValidation())
        .concat(biographyTabValidation())
        .concat(contactTabValidation())
        .concat(anatomyTabValidation())
  }
}

export const convertUserToFormik = (user: UserProfileDraft, unit: HUUnit): UserFormikType => {
  return {
    ...user,
    unit,
    birthdate: user.birthdate?.value ? dayjs(user.birthdate.value).toDate() : undefined,
    birthdateVisibility: user.birthdate?.visibility.all,
    crossfitStartDate: user.crossfitStartDate?.value ? dayjs(user.crossfitStartDate.value).toDate() : undefined,
    crossfitStartDateVisibility: user.crossfitStartDate?.visibility.all,
    genderCategory: user.genderCategory?.value,
    genderCategoryVisibility: user.genderCategory?.visibility.all,
    job: user.job?.value,
    jobVisibility: user.job?.visibility.all,
    nationality: user.nationality?.value,
    nationalityVisibility: user.nationality?.visibility.all,
    ...convertAddressToFormik(user.address),
    phoneNumberCountryCode: user.phoneNumber?.countryCode ?? "FR",
    phoneNumber: user.phoneNumber?.number,
    weight: user.weight?.value,
    weightVisibility: user.weight?.visibility.all,
    height: user.height?.value,
    heightVisibility: user.height?.visibility.all,
  }
}

export function convertUserProfileFromFormikToUserProfileDraft(formik: UserFormikType): UserProfileDraft {
  const parse =
    formik.phoneNumber &&
    formik.phoneNumberCountryCode &&
    parsePhoneNumber(formik.phoneNumber, formik.phoneNumberCountryCode as CountryCode)
  return {
    // Identity form content
    firstname: formik.firstname ?? "",
    lastname: formik.lastname ?? "",
    username: formik.username ?? "",
    birthdate: {
      value: formik.birthdate ? dayjs(formik.birthdate).format(defaultDateFormat) : "",
      visibility: {
        all: formik.birthdateVisibility ?? false,
      },
    },
    // Crossfit form content
    crossfitStartDate: {
      value: formik.crossfitStartDate ? dayjs(formik.crossfitStartDate).format(defaultDateFormat) : "",
      visibility: {
        all: formik.crossfitStartDateVisibility ?? false,
      },
    },
    genderCategory: formik.genderCategory
      ? {
          value: formik.genderCategory,
          visibility: {
            all: formik.genderCategoryVisibility ?? false,
          },
        }
      : undefined,
    // Biography form content
    biography: formik.biography !== "" ? formik.biography : "",
    job: {
      value: formik.job ?? "",
      visibility: {
        all: formik.jobVisibility ?? false,
      },
    },
    nationality: {
      value: formik.nationality ?? "",
      visibility: {
        all: formik.nationalityVisibility ?? false,
      },
    },
    // Contact form content
    phoneNumber:
      formik.phoneNumber && formik.phoneNumberCountryCode && parse
        ? {
            number: parse.nationalNumber,
            countryCode: formik.phoneNumberCountryCode,
          }
        : undefined,
    address: convertAddressFromFormik(formik),
    emergencyInformation: formik.emergencyInformation,
    // Anatomy form content
    weight: {
      value: formik.weight ?? 0,
      visibility: {
        all: formik.weightVisibility ?? false,
      },
    },
    height: {
      value: formik.height ?? 0,
      visibility: {
        all: formik.heightVisibility ?? false,
      },
    },
  }
}

export type UserFormikType = {
  firstname: string
  lastname: string
  username: string
  email?: string
  unit: HUUnit
  birthdate?: Date
  birthdateVisibility?: boolean
  crossfitStartDate?: Date
  crossfitStartDateVisibility?: boolean
  genderCategory?: GenderCategory
  genderCategoryVisibility?: boolean
  biography?: string
  job?: string
  jobVisibility?: boolean
  nationality?: string
  nationalityVisibility?: boolean
  phoneNumberCountryCode?: string
  phoneNumber?: string
  emergencyInformation?: string
  weight?: number
  weightVisibility?: boolean
  height?: number
  heightVisibility?: boolean
} & AddressFormikType
